import React, { createContext, useContext, useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Toast } from '../components/Toast';

export type UpdateCheckStatus = 'idle' | 'checking' | 'up-to-date' | 'error';

interface PWAContextType {
    needRefresh: boolean;
    updateServiceWorker: (reloadPage?: boolean) => Promise<void>;
    checkForUpdate: () => Promise<void>;
    updateCheckStatus: UpdateCheckStatus;
    lastCheckTime: number | null;
    appVersion: string;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

// The interval time to check for updates (1 hour)
const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000;

export const PWAProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [updateCheckStatus, setUpdateCheckStatus] = useState<UpdateCheckStatus>('idle');
    const [lastCheckTime, setLastCheckTime] = useState<number | null>(() => {
        const stored = localStorage.getItem('good_shout_last_check_time');
        return stored ? parseInt(stored, 10) : null;
    });
    
    // 1. Service Worker Logic
    const {
        needRefresh: [needRefresh, setNeedRefresh],
        updateServiceWorker,
        update
    } = useRegisterSW({
        onRegisterError(error) {
            console.error('SW registration error', error);
        },
    });

    const updateLastCheckTime = () => {
        const now = Date.now();
        setLastCheckTime(now);
        localStorage.setItem('good_shout_last_check_time', now.toString());
    };

    const checkForUpdate = async () => {
        if (update) {
            setUpdateCheckStatus('checking');
            try {
                // virtual:pwa-register update function returns a promise or void depending on implementation
                // We await it just in case, and add a minimum delay for UX
                await Promise.all([
                    update(),
                    new Promise(resolve => setTimeout(resolve, 1000)) // Min 1s loading state
                ]);
                
                updateLastCheckTime();
                
                // If we found an update, needRefresh will become true (handled by the hook)
                // If we didn't find one, we want to show 'up-to-date'
                
                // We check needRefresh here, but we also have a useEffect below to catch if it flips to true shortly after
                if (!needRefresh) {
                   setUpdateCheckStatus('up-to-date');
                   // Reset back to idle after a few seconds
                   setTimeout(() => {
                       setUpdateCheckStatus(prev => prev === 'up-to-date' ? 'idle' : prev);
                   }, 3000);
                } else {
                    // Update found, let UI handle it (it likely shows the "Update" button instead of check)
                    setUpdateCheckStatus('idle');
                }

            } catch (e) {
                console.error("Failed to check for update", e);
                setUpdateCheckStatus('error');
                setErrorToastVisible(true);
                // Reset back to idle after a few seconds
                setTimeout(() => {
                    setUpdateCheckStatus(prev => prev === 'error' ? 'idle' : prev);
                }, 3000);
            }
        }
    };

    // Watch for needRefresh changes to prevent race conditions
    // If needRefresh becomes true while we are showing "up-to-date" or "checking", we should clear that status
    useEffect(() => {
        if (needRefresh) {
            setUpdateCheckStatus('idle');
        }
    }, [needRefresh]);

    // Periodic Check for Updates
    useEffect(() => {
        if (!update) return;

        // Check immediately on mount/ready
        update();
        // We don't necessarily want to update the timestamp on every mount, 
        // but maybe we do since it IS a check. Let's do it.
        updateLastCheckTime();

        // Set up interval
        const interval = setInterval(() => {
            update();
            updateLastCheckTime();
        }, UPDATE_CHECK_INTERVAL);

        return () => clearInterval(interval);
    }, [update]);

    // 2. Version Tracking & Error Logic
    const [versionToastVisible, setVersionToastVisible] = useState(false);
    const [errorToastVisible, setErrorToastVisible] = useState(false);
    const [currentVersion, setCurrentVersion] = useState('');
    const appVersion = __APP_VERSION__ || '0.0.0';

    useEffect(() => {
        const lastVersion = localStorage.getItem('last_known_version');

        if (appVersion && lastVersion && appVersion !== lastVersion) {
            setCurrentVersion(appVersion);
            setVersionToastVisible(true);
        }

        if (appVersion) {
            localStorage.setItem('last_known_version', appVersion);
        }
    }, [appVersion]);

    const closeVersionToast = () => setVersionToastVisible(false);
    const closeErrorToast = () => setErrorToastVisible(false);
    const closeUpdateToast = () => setNeedRefresh(false);

    return (
        <PWAContext.Provider value={{
            needRefresh,
            updateServiceWorker,
            checkForUpdate,
            updateCheckStatus,
            lastCheckTime,
            appVersion
        }}>
            {children}
            
            {/* GLOBAL PWA TOASTS */}
            
            {/* Update Available Toast (Global Alert) */}
            <Toast
                type="info"
                message="New version available!"
                isVisible={needRefresh}
                onClose={closeUpdateToast}
                duration={10000}
                action={{
                    label: "Update Now",
                    onClick: () => updateServiceWorker(true)
                }}
            />

            {/* Version Updated Toast */}
            <Toast
                type="success"
                message={`Good Shout updated to v${currentVersion}`}
                isVisible={versionToastVisible}
                onClose={closeVersionToast}
                duration={5000}
            />

            {/* Error Toast */}
            <Toast
                type="error"
                message="Failed to check for updates"
                isVisible={errorToastVisible}
                onClose={closeErrorToast}
                duration={4000}
            />
        </PWAContext.Provider>
    );
};

export const usePWA = () => {
    const context = useContext(PWAContext);
    if (context === undefined) {
        throw new Error('usePWA must be used within a PWAProvider');
    }
    return context;
};
